import { TelemetryPoint } from "../types";
import { E3_CAMERA_WALL_OFFSET, E3_HARDWARE_V_FOV, E3_HARDWARE_H_FOV, E3_EFFECTIVE_FOV_MULTIPLIER } from "./telemetryService";

export interface RouteParams {
  circles: {
    b1: { z: number; r: number };
    c1: { z: number; r: number };
    c2: { z: number; r: number };
    r1: { z: number; r: number };
  };
  standoff: number;
  overlapH: number;
  overlapV: number;
  speed: number;
  cameraTilt?: number; // In degrees
  centerX?: number;
  centerY?: number;
  originalPath?: TelemetryPoint[];
  startAngleDeg?: number;
  lookInward?: boolean;
}

/**
 * Z-YX (Yaw-Pitch-Roll) Quaternion conversion.
 * Matches TrajectoryMap visualizer: 
 * Positive pitch rotates vector (1,0,0) towards negative Z (looking down).
 */
const toQuaternion = (roll: number, pitch: number, yaw: number) => {
  const cy = Math.cos(yaw * 0.5), sy = Math.sin(yaw * 0.5);
  const cp = Math.cos(pitch * 0.5), sp = Math.sin(pitch * 0.5);
  const cr = Math.cos(roll * 0.5), sr = Math.sin(roll * 0.5);
  return {
    w: cy * cp * cr + sy * sp * sr,
    x: cy * cp * sr - sy * sp * cr,
    y: cy * sp * cr + sy * cp * sr,
    z: sy * cp * cr - cy * sp * sr
  };
};

/**
 * Geometric Helper: Calculates surface normal and point with standoff for any Z altitude.
 * This ensures the camera is always perpendicular to the surface trajectory.
 */
const getOffsetPointAtZ = (z: number, params: RouteParams) => {
  const { circles, standoff } = params;
  const { b1, c1, c2, r1 } = circles;

  let surfR: number;
  let dR: number;
  let dZ: number;

  if (z < c1.z) {
    // Segment 1: B1 to C1 (Cone)
    const frac = Math.max(0, (z - b1.z) / Math.max(0.01, c1.z - b1.z));
    surfR = b1.r + (c1.r - b1.r) * frac;
    dR = c1.r - b1.r;
    dZ = c1.z - b1.z;
  } else if (z <= c2.z) {
    // Segment 2: C1 to C2 (Cylinder)
    const frac = Math.max(0, (z - c1.z) / Math.max(0.01, c2.z - c1.z));
    surfR = c1.r + (c2.r - c1.r) * frac;
    dR = c2.r - c1.r;
    dZ = c2.z - c1.z;
  } else {
    // Segment 3: C2 to R1 (Roof)
    const frac = Math.min(1, (z - c2.z) / Math.max(0.01, r1.z - c2.z));
    surfR = c2.r + (r1.r - c2.r) * frac;
    dR = r1.r - c2.r;
    dZ = r1.z - c2.z;
  }

  const len = Math.sqrt(dR * dR + dZ * dZ);
  // Inward normal components (perpendicular to surface slope)
  const nx = -dZ / Math.max(0.001, len);
  const nz = dR / Math.max(0.001, len);

  const physicalStandoff = standoff + E3_CAMERA_WALL_OFFSET;
  const droneR = Math.max(0.01, surfR + physicalStandoff * nx);
  const droneZ = z + physicalStandoff * nz;

  // Pitch: Calculate angle to look directly at the target surface point (surfR, z)
  // vd: vertical diff, rd: radial diff
  const vd = z - droneZ;
  const rd = surfR - droneR;

  // In our coordinate system, positive pitch is looking down.
  // If vd is positive (surface is higher), we want a negative pitch to look up.
  const basePitch = -Math.atan2(vd, rd);
  const tiltRad = ((params.cameraTilt || 0) * Math.PI) / 180;

  return {
    r: droneR,
    z: droneZ,
    pitch: basePitch + tiltRad
  };
};

export const generateHorizontalVesselRoute = (params: RouteParams): TelemetryPoint[] => {
  const { circles, standoff, centerX = 0, centerY = 0, originalPath, overlapH, overlapV, speed } = params;
  const { c1, c2 } = circles;
  const points: TelemetryPoint[] = [];
  let t = 0;

  const waypointInterval = 0.1;
  const targetWallR = c1.r;
  const droneBaseR = targetWallR - standoff - E3_CAMERA_WALL_OFFSET;
  const effectiveOpticalStandoff = Math.max(0.1, standoff);

  const V_FOV_EFF = (E3_HARDWARE_V_FOV * E3_EFFECTIVE_FOV_MULTIPLIER) * (Math.PI / 180);
  const frameH = 2 * effectiveOpticalStandoff * Math.tan(V_FOV_EFF / 2);
  const strideV = frameH * (1 - (overlapV / 100));

  const H_FOV_EFF = (E3_HARDWARE_H_FOV * E3_EFFECTIVE_FOV_MULTIPLIER) * (Math.PI / 180);
  const frameW = 2 * effectiveOpticalStandoff * Math.tan(H_FOV_EFF / 2);
  const strideH = frameW * (1 - (overlapH / 100));

  if (originalPath && originalPath.length > 0) {
    originalPath.filter(p => p.timestamp <= originalPath[0].timestamp + 35).forEach(p => points.push({ ...p, isTransit: true }));
    t = points[points.length - 1].timestamp;
  } else {
    points.push({ timestamp: 0, x: centerX - (c1.r + 5), y: centerY, z: c1.z, qx: 0, qy: 0, qz: 0, qw: 1, quality: 1, tilt: 0, isTransit: true });
    t = 5.0;
  }

  const zStart = c1.z + standoff;
  const zEnd = c2.z - standoff;
  const numRings = Math.max(2, Math.ceil(Math.abs(zEnd - zStart) / strideV));
  const ringStepZ = (zEnd - zStart) / (numRings - 1);

  for (let i = 0; i < numRings; i++) {
    const curZ = zStart + i * ringStepZ;
    const { r, z, pitch: basePitch } = getOffsetPointAtZ(curZ, params);

    // Define the tilts for this ring. 
    // At the absolute boundaries (c1+standoff and c2-standoff), we perform the full tilt sequence.
    let tilts = [basePitch];
    if (i === 0) {
      // Lowest pass at c1.z + standoff: perpendicular, 45 down (+), 90 down (+) looking at bottom
      tilts = [basePitch, basePitch + Math.PI / 4, basePitch + Math.PI / 2];
    } else if (i === numRings - 1) {
      // Highest pass at c2.z - standoff: perpendicular, 45 up (-), 90 up (-) looking at top
      tilts = [basePitch, basePitch - Math.PI / 4, basePitch - Math.PI / 2];
    }

    for (const currentPitch of tilts) {
      const circumference = 2 * Math.PI * r;
      const numSteps = Math.ceil(circumference / (speed * waypointInterval));
      const angleStep = (2 * Math.PI) / numSteps;

      for (let j = 0; j <= numSteps; j++) {
        const angle = (j * angleStep) % (2 * Math.PI);
        const yaw = params.lookInward ? (angle + Math.PI) % (2 * Math.PI) : angle;
        const q = toQuaternion(0, currentPitch, yaw);
        t += waypointInterval;
        points.push({
          timestamp: t,
          x: centerX + r * Math.cos(angle),
          y: centerY + r * Math.sin(angle),
          z: z, // Keep constant ring height
          qx: q.x,
          qy: q.y,
          qz: q.z,
          qw: q.w,
          quality: 1,
          tilt: currentPitch * (180 / Math.PI),
          forceCapture: j % Math.ceil(strideH / (speed * waypointInterval)) === 0,
          lookInward: params.lookInward
        });
      }
    }
  }

  // Final Exit / RTH Segment
  const lastP = points[points.length - 1];
  const entryP = points[0];
  const exitDist = Math.sqrt(Math.pow(lastP.x - entryP.x, 2) + Math.pow(lastP.y - entryP.y, 2) + Math.pow(lastP.z - entryP.z, 2));
  const exitSteps = Math.ceil(exitDist / (speed * 0.2));
  for (let i = 1; i <= exitSteps; i++) {
    const f = i / exitSteps;
    t += 0.2;
    points.push({
      timestamp: t,
      x: lastP.x + (entryP.x - lastP.x) * f,
      y: lastP.y + (entryP.y - lastP.y) * f,
      z: lastP.z + (entryP.z - lastP.z) * f,
      qx: 0, qy: 0, qz: 0, qw: 1,
      quality: 1,
      isTransit: true
    });
  }

  return points;
};

export const generateCylindricalRoute = (params: RouteParams): TelemetryPoint[] => {
  const { circles, standoff, centerX = 0, centerY = 0, originalPath, overlapH, overlapV, startAngleDeg = 180 } = params;
  const { b1, c1, c2, r1 } = circles;
  const points: TelemetryPoint[] = [];
  let t = 0;

  const START_ANGLE_OFFSET = (startAngleDeg * Math.PI) / 180;
  const waypointInterval = 0.1;
  const verticalSpeed = 0.5;

  const zWallFloor = b1.z + standoff;
  const zWallTop = c2.z;

  const ascentSteps = Math.ceil(Math.abs(zWallTop - zWallFloor) / (verticalSpeed * waypointInterval));
  const roofRadialLen = Math.sqrt(Math.pow(c2.r - r1.r, 2) + Math.pow(r1.z - c2.z, 2));
  const roofSteps = Math.max(10, Math.ceil(roofRadialLen / (verticalSpeed * waypointInterval)));

  const opticalStandoff = Math.max(0.1, standoff);
  const H_FOV_EFF = (E3_HARDWARE_H_FOV * E3_EFFECTIVE_FOV_MULTIPLIER) * (Math.PI / 180);
  const frameW = 2 * opticalStandoff * Math.tan(H_FOV_EFF / 2);
  const strideH = frameW * (1 - (overlapH / 100));
  const numStrips = Math.max(4, Math.ceil((2 * Math.PI * c2.r) / strideH));
  const angleStep = (2 * Math.PI) / numStrips;

  const V_FOV_EFF = (E3_HARDWARE_V_FOV * E3_EFFECTIVE_FOV_MULTIPLIER) * (Math.PI / 180);
  const frameH = 2 * opticalStandoff * Math.tan(V_FOV_EFF / 2);
  const strideV = frameH * (1 - (overlapV / 100));

  if (originalPath && originalPath.length > 0) {
    originalPath.filter(p => p.timestamp <= originalPath[0].timestamp + 35).forEach(p => points.push({ ...p, isTransit: true }));
    t = points[points.length - 1].timestamp;
  } else {
    points.push({ timestamp: 0, x: centerX - (c2.r + 5), y: centerY, z: b1.z + 2, qx: 0, qy: 0, qz: 0, qw: 1, quality: 1, tilt: 0, isTransit: true });
    t = 5.0;
  }

  for (let k = 0; k < Math.ceil(numStrips / 2); k++) {
    const angleUp = (k * angleStep + START_ANGLE_OFFSET) % (2 * Math.PI);
    const angleDown = (angleUp + Math.PI) % (2 * Math.PI);
    const nextAngleUp = (((k + 1) * angleStep) + START_ANGLE_OFFSET) % (2 * Math.PI);

    // 1. ASCENT (WALL)
    for (let i = 0; i <= ascentSteps; i++) {
      const zTarget = zWallFloor + (zWallTop - zWallFloor) * (i / ascentSteps);
      const { r, z, pitch } = getOffsetPointAtZ(zTarget, params);
      const q = toQuaternion(0, pitch, angleUp);
      t += waypointInterval;
      points.push({
        timestamp: t, x: centerX + r * Math.cos(angleUp), y: centerY + r * Math.sin(angleUp), z,
        qx: q.x, qy: q.y, qz: q.z, qw: q.w, quality: 1,
        tilt: pitch * (180 / Math.PI),
        forceCapture: (i * verticalSpeed * waypointInterval) % strideV < (verticalSpeed * waypointInterval)
      });
    }

    // 2. ROOF SCAN (INWARD: C2 -> R1)
    for (let i = 1; i <= roofSteps; i++) {
      const zTarget = c2.z + (r1.z - c2.z) * (i / roofSteps);
      const { r, z, pitch } = getOffsetPointAtZ(zTarget, params);
      const q = toQuaternion(0, pitch, angleUp);
      t += waypointInterval;
      points.push({ timestamp: t, x: centerX + r * Math.cos(angleUp), y: centerY + r * Math.sin(angleUp), z, qx: q.x, qy: q.y, qz: q.z, qw: q.w, quality: 1, tilt: pitch * (180 / Math.PI), forceCapture: i === 1 });
    }

    // 3. PEAK TRANSITION (Looking UP - Mirror of Bottom)
    const peakRef = getOffsetPointAtZ(r1.z, params);
    const peakLookUpPitch = -Math.PI / 2; // -90 deg looks straight up

    // Move to center while looking up
    for (let i = 1; i <= 10; i++) {
      const frac = i / 10;
      const curR = peakRef.r * (1 - frac);
      const curP = peakRef.pitch + (peakLookUpPitch - peakRef.pitch) * frac;
      const q = toQuaternion(0, curP, angleUp);
      t += 0.2;
      points.push({ timestamp: t, x: centerX + curR * Math.cos(angleUp), y: centerY + curR * Math.sin(angleUp), z: peakRef.z, qx: q.x, qy: q.y, qz: q.z, qw: q.w, quality: 1, tilt: curP * (180 / Math.PI) });
    }
    // Flip yaw at center
    for (let i = 1; i <= 10; i++) {
      const frac = i / 10;
      const curA = angleUp + (angleDown - angleUp) * frac;
      const q = toQuaternion(0, peakLookUpPitch, curA);
      t += 0.1;
      points.push({ timestamp: t, x: centerX, y: centerY, z: peakRef.z, qx: q.x, qy: q.y, qz: q.z, qw: q.w, quality: 1, tilt: -90 });
    }
    // Move back out to standoff
    for (let i = 1; i <= 10; i++) {
      const frac = i / 10;
      const curR = peakRef.r * frac;
      const curP = peakLookUpPitch + (peakRef.pitch - peakLookUpPitch) * frac;
      const q = toQuaternion(0, curP, angleDown);
      t += 0.2;
      points.push({ timestamp: t, x: centerX + curR * Math.cos(angleDown), y: centerY + curR * Math.sin(angleDown), z: peakRef.z, qx: q.x, qy: q.y, qz: q.z, qw: q.w, quality: 1, tilt: curP * (180 / Math.PI) });
    }

    // 4. ROOF SCAN (OUTWARD: R1 -> C2)
    for (let i = 1; i <= roofSteps; i++) {
      const zTarget = r1.z - (r1.z - c2.z) * (i / roofSteps);
      const { r, z, pitch } = getOffsetPointAtZ(zTarget, params);
      const q = toQuaternion(0, pitch, angleDown);
      t += waypointInterval;
      points.push({ timestamp: t, x: centerX + r * Math.cos(angleDown), y: centerY + r * Math.sin(angleDown), z, qx: q.x, qy: q.y, qz: q.z, qw: q.w, quality: 1, tilt: pitch * (180 / Math.PI) });
    }

    // 5. DESCENT (WALL)
    for (let i = 0; i <= ascentSteps; i++) {
      const zTarget = zWallTop - (zWallTop - zWallFloor) * (i / ascentSteps);
      const { r, z, pitch } = getOffsetPointAtZ(zTarget, params);
      const q = toQuaternion(0, pitch, angleDown);
      t += waypointInterval;
      points.push({
        timestamp: t, x: centerX + r * Math.cos(angleDown), y: centerY + r * Math.sin(angleDown), z,
        qx: q.x, qy: q.y, qz: q.z, qw: q.w, quality: 1,
        tilt: pitch * (180 / Math.PI),
        forceCapture: (i * verticalSpeed * waypointInterval) % strideV < (verticalSpeed * waypointInterval)
      });
    }

    // 6. BOTTOM TRANSITION (Looking DOWN)
    const floorRef = getOffsetPointAtZ(zWallFloor, params);
    const floorLookDownPitch = Math.PI / 2; // +90 deg looks straight down

    for (let i = 1; i <= 10; i++) {
      const frac = i / 10;
      const curR = floorRef.r * (1 - frac);
      const curP = floorRef.pitch + (floorLookDownPitch - floorRef.pitch) * frac;
      const q = toQuaternion(0, curP, angleDown);
      t += 0.2;
      points.push({ timestamp: t, x: centerX + curR * Math.cos(angleDown), y: centerY + curR * Math.sin(angleDown), z: floorRef.z, qx: q.x, qy: q.y, qz: q.z, qw: q.w, quality: 1, tilt: curP * (180 / Math.PI) });
    }
    for (let i = 1; i <= 10; i++) {
      const frac = i / 10;
      const curA = angleDown + (nextAngleUp - angleDown) * frac;
      const q = toQuaternion(0, floorLookDownPitch, curA);
      t += 0.1;
      points.push({ timestamp: t, x: centerX, y: centerY, z: floorRef.z, qx: q.x, qy: q.y, qz: q.z, qw: q.w, quality: 1, tilt: 90 });
    }
    for (let i = 1; i <= 10; i++) {
      const frac = i / 10;
      const curR = floorRef.r * frac;
      const curP = floorLookDownPitch + (floorRef.pitch - floorLookDownPitch) * frac;
      const q = toQuaternion(0, curP, nextAngleUp);
      t += 0.2;
      points.push({ timestamp: t, x: centerX + curR * Math.cos(nextAngleUp), y: centerY + curR * Math.sin(nextAngleUp), z: floorRef.z, qx: q.x, qy: q.y, qz: q.z, qw: q.w, quality: 1, tilt: curP * (180 / Math.PI) });
    }
  }

  // Final Exit / RTH Segment
  const lastP = points[points.length - 1];
  const entryP = points[0];
  const exitDist = Math.sqrt(Math.pow(lastP.x - entryP.x, 2) + Math.pow(lastP.y - entryP.y, 2) + Math.pow(lastP.z - entryP.z, 2));
  const exitSteps = Math.ceil(exitDist / (1.0 * 0.2));
  for (let i = 1; i <= exitSteps; i++) {
    const f = i / exitSteps;
    t += 0.2;
    points.push({
      timestamp: t,
      x: lastP.x + (entryP.x - lastP.x) * f,
      y: lastP.y + (entryP.y - lastP.y) * f,
      z: lastP.z + (entryP.z - lastP.z) * f,
      qx: 0, qy: 0, qz: 0, qw: 1,
      quality: 1,
      isTransit: true
    });
  }

  return points;
};

/**
 * Generates a synthetic point cloud for a cylinder with a bottom cone.
 */
export const generateCylinderPointCloud = (
  radius: number,
  coneHeight: number,
  cylinderHeight: number,
  numPoints: number,
  baseZ: number,
  centerX: number = 0,
  centerY: number = 0
): TelemetryPoint[] => {
  const points: TelemetryPoint[] = [];
  const totalHeight = coneHeight + cylinderHeight;

  for (let i = 0; i < numPoints; i++) {
    const zRel = Math.random() * totalHeight;
    const theta = Math.random() * Math.PI * 2;
    const z = baseZ + zRel;

    let r = radius;
    if (zRel < coneHeight) {
      r = 0.5 + (radius - 0.5) * (zRel / coneHeight);
    }

    const rNoise = r + (Math.random() - 0.5) * 0.05;

    points.push({
      timestamp: 0,
      x: centerX + rNoise * Math.cos(theta),
      y: centerY + rNoise * Math.sin(theta),
      z: z,
      qx: 0,
      qy: 0,
      qz: 0,
      qw: 1,
      quality: 1,
      scalar: 0.5 + Math.random() * 0.2
    });
  }
  return points;
};
